/**
 * Physical Processes List Route
 *
 * Displays searchable, filterable list of physical processes
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useListPageState } from '../../hooks/useListPageState';
import { usePhysicalProcesses } from '../../graphql/queries';
import {
  DataTable,
  EmptyState,
  Pagination,
  PermissionGate,
  TableSkeleton,
  type DataTableColumn,
} from '../../components/shared';
import type { PhysicalProcess } from '../../graphql/types';
import '@tylertech/forge/text-field';
import '@tylertech/forge/select';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './index.css';

export const Route = createFileRoute('/physical-processes/')({
  component: PhysicalProcessesListPage,
  validateSearch: (search: Record<string, unknown>) => ({
    page: Math.max(1, Number(search.page) || 1),
    pageSize: Number(search.pageSize) || 25,
  }),
});

// Export component for testing
export { PhysicalProcessesListPage };

interface PhysicalProcessFilters {
  search: string;
  processType: string;
  processTier: string;
}

const ForgeButton = 'forge-button' as React.ElementType;

function PhysicalProcessesListPage(): JSX.Element {
  const navigate = useNavigate();
  const { page, pageSize } = Route.useSearch();

  // State for filters
  const [filters, setFilters] = useState<PhysicalProcessFilters>({
    search: '',
    processType: '',
    processTier: '',
  });

  // Fetch physical processes with GraphQL
  const { data, loading, error } = usePhysicalProcesses();

  // Extract physical processes from connection
  const physicalProcesses = data?.physicalProcesses?.edges?.map((edge) => edge.node) || [];

  const { sortColumn, sortDirection, handleSort, pagedItems, totalCount } =
    useListPageState<PhysicalProcess>({
      items: physicalProcesses,
      page,
      pageSize,
      filterFn: (physicalProcess) => {
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          const matchesName = physicalProcess.name.toLowerCase().includes(searchLower);
          const matchesDescription = physicalProcess.description
            ?.toLowerCase()
            .includes(searchLower);
          if (!matchesName && !matchesDescription) {
            return false;
          }
        }
        if (filters.processType && physicalProcess.processType !== filters.processType) {
          return false;
        }
        if (filters.processTier && physicalProcess.processTier !== filters.processTier) {
          return false;
        }
        return true;
      },
      sortValue: (physicalProcess, column) =>
        String(physicalProcess[column as keyof PhysicalProcess] ?? ''),
    });

  // DataTable columns
  const columns: DataTableColumn<PhysicalProcess>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      width: '40%',
      render: (physicalProcess) => (
        <div>
          <div className="entity-list-name-cell">{physicalProcess.name}</div>
          {physicalProcess.description && (
            <div className="entity-list-description-cell">
              {physicalProcess.description.substring(0, 60)}
              {physicalProcess.description.length > 60 ? '...' : ''}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'processType',
      label: 'Process Type',
      sortable: true,
      width: '20%',
      render: (physicalProcess) => <span>{physicalProcess.processType || '—'}</span>,
    },
    {
      key: 'processTier',
      label: 'Process Tier',
      sortable: true,
      width: '20%',
      render: (physicalProcess) => <span>{physicalProcess.processTier || '—'}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: '20%',
      render: (physicalProcess) => <span>{physicalProcess.status || '—'}</span>,
    },
  ];

  const handleRowClick = (physicalProcess: PhysicalProcess): void => {
    navigate({ to: `/physical-processes/${physicalProcess.entityId}` });
  };

  const handleFilterChange = (newFilters: Partial<PhysicalProcessFilters>): void => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    void navigate({ to: '/physical-processes', search: { page: 1, pageSize } });
  };

  const handleClearFilters = (): void => {
    setFilters({ search: '', processType: '', processTier: '' });
    void navigate({ to: '/physical-processes', search: { page: 1, pageSize } });
  };

  const handlePageChange = (newPage: number): void => {
    void navigate({ to: '/physical-processes', search: { page: newPage, pageSize } });
  };

  const handlePageSizeChange = (newPageSize: number): void => {
    void navigate({
      to: '/physical-processes',
      search: { page: 1, pageSize: newPageSize },
    });
  };

  if (error) {
    return (
      <div className="physical-processes-list-page">
        <h1 className="forge-typography--headline4">Physical Processes</h1>
        <div className="entity-list-error-state">
          <EmptyState
            icon="error"
            title="Failed to load physical processes"
            description={error.message}
            actionLabel="Retry"
            onAction={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="physical-processes-list-page">
      <div className="physical-processes-header">
        <div>
          <h1 className="forge-typography--headline4">Physical Processes</h1>
          <p className="forge-typography--body2 page-header-subtitle">
            {loading ? 'Loading...' : `${totalCount} physical processes`}
          </p>
        </div>
        <PermissionGate permission="physical-process:create">
          <ForgeButton
            type="button"
            variant="raised"
            onClick={() => navigate({ to: '/physical-processes/new' })}
          >
            Create Physical Process
          </ForgeButton>
        </PermissionGate>
      </div>

      {/* Filters */}
      <div className="filters-row">
        <div className="search-field">
          <input
            type="text"
            className="search-input"
            placeholder="Search physical processes..."
            aria-label="Search physical processes"
            value={filters.search}
            onChange={(e) => handleFilterChange({ search: e.target.value })}
          />
        </div>

        <select
          className="filter-select"
          aria-label="Filter by process type"
          value={filters.processType}
          onChange={(e) => handleFilterChange({ processType: e.target.value })}
        >
          <option value="">All Process Types</option>
          <option value="core">Core</option>
          <option value="supporting">Supporting</option>
          <option value="management">Management</option>
        </select>

        <select
          className="filter-select"
          aria-label="Filter by process tier"
          value={filters.processTier}
          onChange={(e) => handleFilterChange({ processTier: e.target.value })}
        >
          <option value="">All Process Tiers</option>
          <option value="strategic">Strategic</option>
          <option value="operational">Operational</option>
          <option value="tactical">Tactical</option>
        </select>

        {(filters.search || filters.processType || filters.processTier) && (
          <ForgeButton type="button" variant="outlined" onClick={handleClearFilters}>
            Clear Filters
          </ForgeButton>
        )}
      </div>

      {/* Data Table */}
      <div className="physical-processes-table-container">
        {loading ? (
          <TableSkeleton rows={10} columns={4} />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={pagedItems}
              getRowKey={(physicalProcess) => physicalProcess.entityId}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              onRowClick={handleRowClick}
              emptyState={{
                icon: 'inbox',
                title: 'No physical processes found',
                description:
                  filters.search || filters.processType || filters.processTier
                    ? 'Try adjusting your filters'
                    : 'Create your first physical process to get started',
                actionLabel: 'Create Physical Process',
                onAction: () => navigate({ to: '/physical-processes/new' }),
              }}
            />
            <Pagination
              totalCount={totalCount}
              pageSize={pageSize}
              currentPage={page}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </div>
    </div>
  );
}
